/**
 * Commercial premises and garages (2026-10-21): the two categories that
 * sit beside flats, houses and land, and the kinds inside them. Mirrors
 * the backend's services/property_kinds (the index's `property_kind`).
 *
 * They get no valuation (a garage model missed by 52-61%), so every
 * surface shows them by what a business buyer compares: kind, m², price
 * per m², floor, street frontage, deed; in place of the AI verdict the
 * usual price per m² of the same kind nearby (backend kind_comparables).
 */
import type { TranslationKey } from "@/lib/i18n";

export type SeparateCategory = "commercial" | "garage";

// bina.az's two commercial categories, by name (owner, 2026-10-21: "the
// commercial should have two types: office and objects").
export const COMMERCIAL_KINDS = ["office", "obyekt"] as const;

/** Offices carry a room count (96% of their ads); shops, premises and
 *  garages mostly do not (about a third), so the rooms filter shows only
 *  once offices alone are picked (owner, 2026-10-23: "the relevant
 *  filters for the type being selected"). */
export function officesOnly(category: string | null | undefined, kinds: readonly string[]): boolean {
  return category === "commercial" && kinds.length > 0 && kinds.every((k) => k === "office");
}
export const GARAGE_KINDS = ["garage", "parking"] as const;
export type PropertyKind = (typeof COMMERCIAL_KINDS)[number] | (typeof GARAGE_KINDS)[number];

export function isSeparate(category: string | null | undefined): category is SeparateCategory {
  return category === "commercial" || category === "garage";
}

/** The kinds a category is split into (empty for homes and land). */
export function kindsOf(category: string | null | undefined): readonly PropertyKind[] {
  return category === "commercial" ? COMMERCIAL_KINDS : category === "garage" ? GARAGE_KINDS : [];
}

/** The filter chip's label ("Ofis", "Obyekt"). */
export function kindChipKey(kind: string): TranslationKey {
  return `listings.kind.${kind}` as TranslationKey;
}

/** What one listing is, as its card and page name it ("Ofis",
 *  "Obyekt"): an obyekt or a garage when the kind is unknown. */
export function kindNounKey(category: string | null | undefined, kind: string | null | undefined): TranslationKey {
  if (kind && (kindsOf(category) as readonly string[]).includes(kind)) {
    return `listings.kind_noun.${kind}` as TranslationKey;
  }
  return (category === "garage" ? "listings.kind_noun.garage" : "listings.kind_noun.obyekt") as TranslationKey;
}

/** The listings page's kind picker: a home, land, a commercial space or a garage. */
export type ListingKind = "" | "apartment" | "house" | "land" | "commercial" | "garage";
export const LISTING_KINDS: readonly Exclude<ListingKind, "">[] = ["apartment", "house", "land", "commercial", "garage"];

/** What changing the kind clears: the filters that mean nothing for the
 *  kind picked (a plot has no rooms, a shop no residence, a garage no
 *  floor). Kept in one place so the bar and the filters sheet agree. */
export function kindChangePatch(k: ListingKind, build: string[], sort?: string | null): Record<string, string | string[] | null> {
  const keepBuild = k === "apartment" ? build : [];
  const patch: Record<string, string | string[] | null> = {
    category: k || null, listing_kind: keepBuild.length ? keepBuild : null, property_kind: null,
  };
  if (k === "land") Object.assign(patch, { rooms: null, floor_min: null, floor_max: null, total_floors_min: null, total_floors_max: null, exclude_first_floor: null, exclude_last_floor: null, area_min: null, area_max: null });
  else Object.assign(patch, { sot_min: null, sot_max: null, pps_min: null, pps_max: null });
  if (k === "commercial" || k === "garage") {
    // No rooms, residence, build, valuation, yield, mortgage, photo tier
    // or amenities for a shop or a garage; a garage has no floor either.
    Object.assign(patch, {
      rooms: null, building_type: null, complex_slug: null, developer: null, deal_tier: null, min_yield: null,
      has_mortgage: null, mortgage_kind: null, max_initial: null, mortgage_rate_min: null, mortgage_rate_max: null,
      mortgage_terms_known: null, mortgage_years: null, photo_condition: null, tags: null, deed_gap: null,
      rent_period: null, exclude_first_floor: null, exclude_last_floor: null, total_floors_min: null, total_floors_max: null,
    });
    if (k === "garage") Object.assign(patch, { floor_min: null, floor_max: null });
    // "Best deal" and "yield" order by the valuation these rows lack.
    if (sort === "best_deal" || sort === "yield") patch.sort = null;
  } else {
    // Price per m² is a filter only shops and garages show.
    Object.assign(patch, { ppm_min: null, ppm_max: null });
  }
  return patch;
}
